import { useMemo } from 'react';
import { useHistory } from '@useHistory/useHistoryCompat';
import { DESTINATION_TYPES } from '../../destination/constants/destinationTypes';
import { coerceBooleanProperty } from '../../../core/utils/coercion';
import { CreateActivateAngularticsService } from '../../../core/services/create-activate-angulartics.service';
import {
  TRACKER_NEW_ACTIVATION_FAILED,
  TRACKER_NEW_ACTIVATION_SUBMIT_CLICK,
  TRACKER_NEW_ACTIVATION_SUCCESS
} from '../tracking';
import { DestinationMode } from '../../../core/interfaces/destination-mode';
import { HevoEntity } from '../../../core/interfaces/hevo-entity';
import { RecentEntitiesService } from '../../../core/services/recent-entities.service';
import { Policy, PolicyTypeEnum } from '../../scheduler/ExecutionPolicyDialog/models';
import { HdWizardStepMeta } from '../../../wizard/wizard-native/interface';
import { HdWizardStepTracker } from '../../../wizard/wizard-native/step-tracker';
import {
  AccountEntityTypes,
  AuthAccountProvider
} from '../../../connector/auth-select/interfaces';
import { TARGET_TYPES, TargetType } from '../../activate-target/models/target-type';
import { useStateMachine } from '../../../wizard/hooks/useStateMachine';
import useAnalyticsTracker from '../../../analytics/hooks/useAnalyticsTracker';
import useService from '../../../injector/hooks/useService';
import { SelectTargetType } from '../../activate-target/create/SelectTargetType';
import { TargetSettings } from '../../activate-target/create/TargetSettings';
import { SelectTarget } from '../../activate-target/SelectTarget';
import { DestinationSettings } from '../../destination/create-destination/components/destination-settings/DestinationSettings';
import { SelectDestinationType } from '../../destination/create-destination/components/select-destination-type-step/SelectDestinationType';
import { AuthSelect } from '../../../connector/auth-select/components/auth-select/AuthSelect';
import { SelectGoogleAuth } from '../../../connector/service-account-auth-select/components/auth-select/ServiceAccountAuthSelect';
import { SelectDestination } from '../../pipeline/create-pipeline/components/select-destination/SelectDestination';
import ActivationsAPI from '../api/ActivationsAPI';
import { ActivationSourceSettings } from './ActivationSourceSettings';
import { FieldsMappingStep } from './FieldsMappingStep';

class DestTypeResolver {
  resolve(tracker: HdWizardStepTracker) {
    return tracker.getStepData('connect-warehouse/select-warehouse-type')['destinationType'];
  }
}

class TargetAuthProviderResolver {
  resolve(tracker: HdWizardStepTracker): AuthAccountProvider {
    const targetTypeInfo = tracker.getStepData('connect-target/select-target-type').targetType;
    const targetType = TARGET_TYPES[targetTypeInfo.type];
    return {
      type: targetType.authProvider,
      connectorType: targetType.name,
      typeDisplay: targetType.displayName,
      entityType: AccountEntityTypes.TARGET,
      authAccountEnvironmentsMetaData: targetType.authAccountsEnvironmentsMetaData,
      isAccountTypeGoogle: targetType.isAuthTypeGoogle || false
    };
  }
}

export function useActivationStateMachine() {
  const history = useHistory();
  const { eventTrack } = useAnalyticsTracker();
  const recentEntitiesService = useService(RecentEntitiesService);
  const _creationTrackingAngularticsService = useService(CreateActivateAngularticsService);

  const getWarehouseFromTrackerStepData = (tracker: HdWizardStepTracker) => {
    const existingWarehouse = tracker.getStepData('connect-warehouse/select-warehouse')[
        'selectedDestination'
      ],
      newWarehouseFromConfig = tracker.getStepData('connect-warehouse/connection-settings')[
        'destination'
      ];

    return existingWarehouse ? existingWarehouse : newWarehouseFromConfig;
  };

  const getTargetFromTrackerStepData = (tracker: HdWizardStepTracker) => {
    const existingTarget = tracker.getStepData('connect-target/select-target')['selectedTarget'],
      newTargetFromAuthAccount = tracker.getStepData('connect-target/select-auth-account')[
        'target'
      ],
      newTargetFromConfig = tracker.getStepData('connect-target/connection-settings')['targetData'];

    return existingTarget
      ? existingTarget
      : newTargetFromAuthAccount
      ? newTargetFromAuthAccount
      : newTargetFromConfig;
  };

  const createActivation = (tracker: HdWizardStepTracker) => {
    const selectedWarehouse = getWarehouseFromTrackerStepData(tracker);
    const selectedTarget = getTargetFromTrackerStepData(tracker);

    const destinationId = selectedWarehouse.id;
    const targetId = selectedTarget.id;

    const warehousePollConfig = {
      mode: 'QUERY',
      query: tracker.getStepData('write-query/select-target-object')['selectedQuery']
    };

    const dataMapping = {
      field_mappings: tracker.getStepData('map-fields/mapping').mapping
    };

    const warehouseFields = {
      warehouse_fields: tracker.getStepData('map-fields/mapping').warehouseFields
    };

    const targetSyncConfig = getTargetSyncConfig(tracker);

    const executionPolicy = new Policy({});

    if (executionPolicy.type === PolicyTypeEnum.NO_SCHEDULE) {
      executionPolicy.type = PolicyTypeEnum.FREQUENCY;
    }

    const activationName = tracker.getStepData('map-fields/mapping').activationName;

    const params: any = {
      name: activationName,
      warehouse_id: destinationId,
      target_id: targetId,
      warehouse_poll_config: warehousePollConfig,
      data_mapping: dataMapping,
      warehouse_query_fields: warehouseFields,
      target_sync_config: targetSyncConfig,
      execution_policy: executionPolicy.getRawData()
    };

    trackNewActivationClick(params, selectedWarehouse, selectedTarget);

    ActivationsAPI.createActivation(params).then(
      res => {
        recentEntitiesService.setRecent(HevoEntity.ACTIVATION.value, res.data);

        eventTrack({
          action: TRACKER_NEW_ACTIVATION_SUCCESS,
          properties: {
            activation: res.data
          }
        });

        _creationTrackingAngularticsService.endSession();

        history.push(`/activation/${res.data.seq_id}/overview`);
      },
      err => {
        eventTrack({
          action: TRACKER_NEW_ACTIVATION_FAILED,
          properties: {
            error: err
          }
        });
      }
    );
  };

  const trackNewActivationClick = (createActivationParams, warehouse, target) => {
    const destinationType = warehouse.dest_type;
    const targetType = 'targetType' in target ? target.targetType : target.type;

    eventTrack({
      action: TRACKER_NEW_ACTIVATION_SUBMIT_CLICK,
      properties: {
        creationParams: createActivationParams,
        destinationType: destinationType,
        targetType: targetType
      }
    });
  };

  const getTargetSyncConfig = (tracker: HdWizardStepTracker) => {
    const mappingSettings = tracker.getStepData('map-fields/mapping')?.mappingSettings;

    if (!mappingSettings) {
      return;
    }

    return mappingSettings.convertConfigToRawData();
  };

  const steps = useMemo<HdWizardStepMeta[]>(() => {
    return [
      {
        path: 'connect-warehouse/select-warehouse',
        component: SelectDestination,
        params: {
          hevoEntityType: HevoEntity.ACTIVATION
        },
        getNextStep: (tracker: HdWizardStepTracker, self: HdWizardStepMeta) => {
          const destination: any = tracker.getStepData(self.path).selectedDestination;

          if (!destination) {
            return 'connect-warehouse/select-warehouse-type';
          } else {
            return 'connect-target/select-target';
          }
        },
        resolve: {
          filterMethod: {
            resolve: () => {
              return (items: any[]) =>
                items.filter(item => {
                  if (item.dest_type === 'REDSHIFT' && item.config?.is_serverless) {
                    return false;
                  }

                  return DESTINATION_TYPES[item.dest_type].isActivateDestination;
                });
            }
          },
          destinationMode: DestinationMode.WAREHOUSE
        }
      },
      {
        path: 'connect-warehouse/select-warehouse-type',
        component: SelectDestinationType,
        params: {
          hevoEntityType: HevoEntity.ACTIVATION
        },
        getNextStep: () => {
          return 'connect-warehouse/connection-settings';
        },
        resolve: {
          filterMethod: {
            resolve: () => {
              return (destinationTypes: any[]) => {
                return destinationTypes.filter(
                  item => DESTINATION_TYPES[item.dest_type].isActivateDestination
                );
              };
            }
          },
          destinationMode: DestinationMode.WAREHOUSE
        }
      },
      {
        path: 'connect-warehouse/connection-settings',
        component: DestinationSettings,
        getNextStep: () => {
          return 'connect-target/select-target';
        },
        params: {
          wizard: 'CREATE_ACTIVATION',
          loginNextURL: '/activation/create',
          hevoEntityType: HevoEntity.ACTIVATION
        },
        resolve: {
          destinationType: new DestTypeResolver(),
          destinationMode: DestinationMode.WAREHOUSE
        }
      },
      {
        path: 'connect-target/select-target',
        component: SelectTarget,
        getNextStep: (tracker: HdWizardStepTracker, self: HdWizardStepMeta) => {
          const target: any = tracker.getStepData(self.path).selectedTarget;

          if (!target) {
            return 'connect-target/select-target-type';
          } else {
            return 'write-query/select-target-object';
          }
        }
      },
      {
        path: 'connect-target/select-target-type',
        component: SelectTargetType,
        getNextStep: (tracker: HdWizardStepTracker, self: HdWizardStepMeta) => {
          const targetTypeRef: TargetType =
            TARGET_TYPES[tracker.getStepData(self.path).targetType.type];

          if (coerceBooleanProperty(targetTypeRef.requireServiceAuth)) {
            return 'connect-target/select-auth-type';
          }

          if (coerceBooleanProperty(targetTypeRef.requireAuth)) {
            return 'connect-target/select-auth-account';
          }

          return 'connect-target/connection-settings';
        }
      },
      {
        path: 'connect-target/select-auth-type',
        component: SelectGoogleAuth,
        getNextStep: () => {
          return 'connect-target/connection-settings';
        },
        resolve: {
          provider: new TargetAuthProviderResolver()
        }
      },
      {
        path: 'connect-target/select-auth-account',
        component: AuthSelect,
        getNextStep: (tracker: HdWizardStepTracker, self: HdWizardStepMeta) => {
          const target = tracker.getStepData(self.path).target;
          if (target) {
            return 'write-query/select-target-object';
          }

          return 'connect-target/connection-settings';
        },
        params: {
          entityType: 'target'
        },
        resolve: {
          provider: new TargetAuthProviderResolver(),
          targetType: {
            resolve: (tracker: HdWizardStepTracker) => {
              return tracker.getStepData('connect-target/select-target-type').targetType.type;
            }
          }
        }
      },
      {
        path: 'connect-target/connection-settings',
        component: TargetSettings,
        getNextStep: () => {
          return 'write-query/select-target-object';
        },
        resolve: {
          targetType: {
            resolve: (tracker: HdWizardStepTracker) => {
              return tracker.getStepData('connect-target/select-target-type').targetType;
            }
          },
          authAccount: {
            resolve: (tracker: HdWizardStepTracker) => {
              const authAccountStepData = tracker.getStepData('connect-target/select-auth-account')
                ?.account
                ? tracker.getStepData('connect-target/select-auth-account')
                : tracker.getStepData('connect-target/select-auth-type');

              return authAccountStepData ? authAccountStepData.account : undefined;
            }
          },
          authType: {
            resolve: (tracker: HdWizardStepTracker) => {
              const authAccountTypeData = tracker.getStepData('connect-target/select-auth-type');
              return authAccountTypeData ? authAccountTypeData.authType : undefined;
            }
          }
        }
      },
      {
        path: 'write-query/select-target-object',
        component: ActivationSourceSettings,
        getNextStep: () => {
          return 'map-fields/mapping';
        },
        resolve: {
          destination: {
            resolve: (tracker: HdWizardStepTracker) => {
              return getWarehouseFromTrackerStepData(tracker);
            }
          },
          target: {
            resolve: (tracker: HdWizardStepTracker) => {
              return getTargetFromTrackerStepData(tracker);
            }
          }
        }
      },
      {
        path: 'map-fields/mapping',
        component: FieldsMappingStep,
        getNextStep: (tracker: HdWizardStepTracker) => {
          createActivation(tracker);
          return undefined;
        },
        resolve: {
          destinationSettings: {
            resolve: (tracker: HdWizardStepTracker) => {
              return getWarehouseFromTrackerStepData(tracker);
            }
          },
          targetSettings: {
            resolve: (tracker: HdWizardStepTracker) => {
              return getTargetFromTrackerStepData(tracker);
            }
          },
          query: {
            resolve: (tracker: HdWizardStepTracker) => {
              return tracker.getStepData('write-query/select-target-object')['selectedQuery'];
            }
          },
          selectedTargetObject: {
            resolve: (tracker: HdWizardStepTracker) => {
              return tracker.getStepData('write-query/select-target-object')[
                'selectedTargetObject'
              ];
            }
          }
        }
      }
    ];
  }, []);

  const getInitialPath = () => {
    return 'connect-warehouse/select-warehouse';
  };

  return useStateMachine({
    steps,
    getInitialPath
  });
}
